// Public UI adapted from Klargo-Reference/pages/SecurityAndFraud.tsx.
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from '../adapters/Helmet.jsx';
import { ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion.jsx';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible.jsx';
import Header from '../components/layout/Header.jsx';
import Footer from '../components/layout/Footer.jsx';
import { LoginDialog } from '../components/auth/LoginDialog.jsx';
import { ForwardArrow, RowChevron, VerifiedTick } from '../components/ds/StatusMark.jsx';
import { useLanguage } from '../components/layout/LanguageSwitcher.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
const SITE_URL = 'https://klargo.se';
const labelClass = 'text-[13px] font-semibold tracking-[0.02em] text-ds-ink';
const numClass = 'font-ds-mono text-[13px] font-medium tracking-[0.08em] text-ds-muted';
const proseClass = 'max-w-[62ch] text-[15px] leading-[1.6] text-ds-text [text-wrap:pretty]';
const registerItemClass = 'border-b border-ds-rule-row';
const registerTriggerClass = 'items-start gap-6 pb-[12px] pt-[18px] text-left hover:no-underline [&>svg]:mt-[4px] [&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg]:shrink-0 [&>svg]:stroke-[1.6] [&>svg]:text-ds-muted';
const registerTitleClass = 'font-ds-serif text-[16px] font-semibold leading-[1.35] text-ds-ink md:text-[17px]';
const registerContentClass = 'max-w-[62ch] pb-[22px] pr-6 text-[15px] leading-[1.6] text-ds-text';
const RULE_CELLS = ['border-b md:pr-8', 'border-b md:border-l md:pl-8', 'border-b md:pr-8', 'border-b md:border-l md:pl-8'];
const Chapter = ({ id, kicker, title, lead, emphasis = false, anchorId, children, }) => (<section id={id} className="scroll-mt-28">
    {anchorId && <div id={anchorId} className="scroll-mt-28"/>}
    {kicker && <div className="mb-1.5 text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{kicker}</div>}
    <h2 className="m-0 font-ds-serif text-[clamp(23px,2.5vw,29px)] font-bold leading-[1.15] tracking-[-0.01em] text-ds-ink [text-wrap:balance]">
      {title}
    </h2>
    {lead && <p className="mb-0 mt-3 max-w-[62ch] text-[15.5px] leading-[1.6] text-ds-text [text-wrap:pretty]">{lead}</p>}
    <div className={`mt-6 ${emphasis ? 'border-t-2 border-ds-ink' : 'border-t border-ds-rule-edge'}`}>{children}</div>
  </section>);
const TickList = ({ items, className }) => (<ul className={['m-0 list-none space-y-2 p-0', className].filter(Boolean).join(' ')}>
    {items.map((item) => (<li key={item} className="flex items-start gap-3 text-[15px] leading-[1.55] text-ds-body">
        <VerifiedTick className="mt-[6px] h-[11px] w-[13px]"/>
        <span>{item}</span>
      </li>))}
  </ul>);
const Callout = ({ tone, label, items }) => (<div className={`border-l-[3px] px-5 py-4 ${tone === 'positive' ? 'border-ds-green bg-ds-green-bg' : 'border-ds-amber bg-ds-amber-bg'}`}>
    <p className={`m-0 mb-2.5 text-[13px] font-semibold tracking-[0.02em] ${tone === 'positive' ? 'text-ds-green-deep' : 'text-ds-amber-fg'}`}>
      {label}
    </p>
    <ul className="m-0 list-none space-y-2 p-0">
      {items.map((item) => (<li key={item} className="flex items-start gap-3 text-[14.5px] leading-[1.5] text-ds-body">
          {tone === 'positive' ? (<VerifiedTick className="mt-[6px] h-[11px] w-[13px]"/>) : (<span aria-hidden="true" className="mt-[8px] h-[6px] w-[6px] shrink-0 rounded-full bg-ds-amber"/>)}
          <span>{item}</span>
        </li>))}
    </ul>
  </div>);
const ChecklistLedger = ({ title, items }) => (<div>
    <h3 className="m-0 border-b border-ds-rule-edge pb-3 font-ds-serif text-[19px] font-semibold leading-[1.3] tracking-[-0.005em] text-ds-ink">
      {title}
    </h3>
    <ol className="m-0 list-none p-0">
      {items.map((item, index) => (<li key={item.title} className="grid grid-cols-[36px_minmax(0,1fr)] gap-x-3 border-b border-ds-rule-row py-3.5">
          <span className={`${numClass} pt-[3px]`}>{`0${index + 1}`}</span>
          <div>
            <p className="m-0 text-[15px] font-semibold leading-[1.4] text-ds-ink">{item.title}</p>
            <ul className="mb-0 mt-1 list-none space-y-0.5 p-0 text-[14px] leading-[1.5] text-ds-text">
              {item.points.map((point) => (<li key={point}>{point}</li>))}
            </ul>
          </div>
        </li>))}
    </ol>
  </div>);
const SecurityAndFraud = ({ isEnglish = false }) => {
    const { t, languageCode } = useLanguage();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('hero');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [entryOpen, setEntryOpen] = useState(false);
    useEffect(() => {
        if (isEnglish && languageCode === 'sv') {
            navigate('/sakerhet-och-bedragerier', { replace: true });
        }
        else if (!isEnglish && languageCode === 'en') {
            navigate('/en/security-and-fraud', { replace: true });
        }
    }, [languageCode, isEnglish, navigate]);
    useEffect(() => {
        const handleScroll = () => {
            const sections = ['hero', 'akut-detaljer', 'seriost-vs-bedragare', 'forebygg', 'checklista-kopa', 'vanliga-bedragerier', 'digital-sakerhet', 'skulder-rattigheter', 'hur-klargo-hjalper', 'faq-bedrageri', 'ordlista'];
            const triggerLine = 200;
            let currentSection = sections[0];
            for (const sectionId of sections) {
                const element = document.getElementById(sectionId);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    if (rect.top <= triggerLine) {
                        currentSection = sectionId;
                    }
                }
            }
            setActiveSection(currentSection);
        };
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    const scrollToSection = (id) => {
        setMobileNavOpen(false);
        requestAnimationFrame(() => {
            const element = document.getElementById(id);
            if (element) {
                const offset = 100;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({
                    top: elementPosition - offset,
                    behavior: 'smooth'
                });
            }
        });
    };
    const detailedSteps = [
        { id: 'step-0', title: t('security.emergency.step1.title'), text: t('security.emergency.step1.text') },
        { id: 'step-1', title: t('security.emergency.step2.title'), text: t('security.emergency.step2.text') },
        { id: 'step-2', title: t('security.emergency.step3.title'), text: t('security.emergency.step3.text') },
        { id: 'step-3', title: t('security.emergency.step4.title'), text: t('security.emergency.step4.text') },
        { id: 'step-4', title: t('security.emergency.step5.title'), text: t('security.emergency.step5.text') },
        { id: 'step-5', title: t('security.emergency.step6.title'), text: t('security.emergency.step6.text') },
    ];
    const navGroups = [{
            title: t('security.nav.overview'),
            items: [{ id: 'hero', label: t('security.nav.overview') }]
        }, {
            title: t('security.nav.emergency'),
            items: [{ id: 'akut-detaljer', label: t('security.nav.ifYouSuspect') }]
        }, {
            title: t('security.nav.beforeDeal'),
            items: [
                { id: 'seriost-vs-bedragare', label: t('security.nav.legitimateVsFraudsters') },
                { id: 'forebygg', label: t('security.nav.fourRules') },
                { id: 'checklista-kopa', label: t('security.nav.checklists') },
                { id: 'vanliga-bedragerier', label: t('security.nav.commonFrauds') },
                { id: 'digital-sakerhet', label: t('security.nav.digitalSecurity') }
            ]
        }, {
            title: t('security.nav.rightsSupport'),
            items: [
                { id: 'skulder-rattigheter', label: t('security.nav.debtsRights') },
                { id: 'hur-klargo-hjalper', label: t('security.nav.howKlargoHelps') },
                { id: 'faq-bedrageri', label: t('security.nav.faq') }
            ]
        }, {
            title: t('security.nav.glossary'),
            items: [{ id: 'ordlista', label: t('security.nav.glossary') }]
        }];
    const showGroupTitle = (group) => !(group.items.length === 1 && group.items[0].label === group.title);
    const buyerChecklist = [
        { title: t('security.buyer.check1.title'), points: [t('security.buyer.check1.point1'), t('security.buyer.check1.point2')] },
        { title: t('security.buyer.check2.title'), points: [t('security.buyer.check2.point1'), t('security.buyer.check2.point2')] },
        { title: t('security.buyer.check3.title'), points: [t('security.buyer.check3.point1'), t('security.buyer.check3.point2')] },
        { title: t('security.buyer.check4.title'), points: [t('security.buyer.check4.point1'), t('security.buyer.check4.point2')] },
        { title: t('security.buyer.check5.title'), points: [t('security.buyer.check5.point1'), t('security.buyer.check5.point2')] },
        { title: t('security.buyer.check6.title'), points: [t('security.buyer.check6.point1'), t('security.buyer.check6.point2')] },
        { title: t('security.buyer.check7.title'), points: [t('security.buyer.check7.point1')] }
    ];
    const sellerChecklist = [
        { title: t('security.seller.check1.title'), points: [t('security.seller.check1.point1'), t('security.seller.check1.point2')] },
        { title: t('security.seller.check2.title'), points: [t('security.seller.check2.point1'), t('security.seller.check2.point2')] },
        { title: t('security.seller.check3.title'), points: [t('security.seller.check3.point1'), t('security.seller.check3.point2')] },
        { title: t('security.seller.check4.title'), points: [t('security.seller.check4.point1')] },
        { title: t('security.seller.check5.title'), points: [t('security.seller.check5.point1')] },
        { title: t('security.seller.check6.title'), points: [t('security.seller.check6.point1')] },
        { title: t('security.seller.check7.title'), points: [t('security.seller.check7.point1'), t('security.seller.check7.point2')] }
    ];
    const fourRules = [
        { title: t('security.fourRules.rule1.title'), points: [t('security.fourRules.rule1.point1'), t('security.fourRules.rule1.point2')] },
        { title: t('security.fourRules.rule2.title'), points: [t('security.fourRules.rule2.point1'), t('security.fourRules.rule2.point2')] },
        { title: t('security.fourRules.rule3.title'), points: [t('security.fourRules.rule3.point1'), t('security.fourRules.rule3.point2')] },
        { title: t('security.fourRules.rule4.title'), points: [t('security.fourRules.rule4.point1'), t('security.fourRules.rule4.point2')] }
    ];
    const fraudTypes = [
        { title: t('security.fraud1.title'), desc: t('security.fraud1.desc'), tips: [t('security.fraud1.tip1'), t('security.fraud1.tip2')] },
        { title: t('security.fraud2.title'), desc: t('security.fraud2.desc'), tips: [t('security.fraud2.tip1'), t('security.fraud2.tip2')] },
        { title: t('security.fraud3.title'), desc: t('security.fraud3.desc'), tips: [t('security.fraud3.tip1'), t('security.fraud3.tip2')] },
        { title: t('security.fraud4.title'), desc: t('security.fraud4.desc'), tips: [t('security.fraud4.tip1'), t('security.fraud4.tip2')] },
        { title: t('security.fraud5.title'), desc: t('security.fraud5.desc'), tips: [t('security.fraud5.tip1'), t('security.fraud5.tip2')] }
    ];
    const faqItems = [
        { q: t('security.faq.q1'), a: t('security.faq.a1') },
        { q: t('security.faq.q2'), a: t('security.faq.a2') },
        { q: t('security.faq.q3'), a: t('security.faq.a3') },
        { q: t('security.faq.q4'), a: t('security.faq.a4') }
    ];
    const glossary = [
        { term: t('security.glossary.term1'), def: t('security.glossary.def1') },
        { term: t('security.glossary.term2'), def: t('security.glossary.def2') },
        { term: t('security.glossary.term3'), def: t('security.glossary.def3') },
        { term: t('security.glossary.term4'), def: t('security.glossary.def4') },
        { term: t('security.glossary.term5'), def: t('security.glossary.def5') },
        { term: t('security.glossary.term6'), def: t('security.glossary.def6') }
    ];
    const metadata = [
        { label: t('security.quickOverview.appliesTo'), value: t('security.quickOverview.appliesToValue') },
        { label: t('security.quickOverview.forWhom'), value: t('security.quickOverview.forWhomValue') },
        { label: t('security.quickOverview.lastUpdated'), value: t('security.quickOverview.lastUpdatedValue') }
    ];
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": t('security.breadcrumb.home'),
                "item": `${SITE_URL}/`
            }, {
                "@type": "ListItem",
                "position": 2,
                "name": t('security.breadcrumb.title'),
                "item": isEnglish ? `${SITE_URL}/en/security-and-fraud` : `${SITE_URL}/sakerhet-och-bedragerier`
            }]
    };
    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [{
                "@type": "Question",
                "name": t('security.faq.q1'),
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": t('security.faq.a1')
                }
            }, {
                "@type": "Question",
                "name": t('security.faq.q2'),
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": t('security.faq.a2')
                }
            }, {
                "@type": "Question",
                "name": t('security.faq.q3'),
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": t('security.faq.a3')
                }
            }, {
                "@type": "Question",
                "name": t('security.faq.q4'),
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": t('security.faq.a4')
                }
            }]
    };
    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": t('security.hero.title'),
        "dateModified": "2025-12-13",
        "datePublished": "2025-01-01",
        "inLanguage": isEnglish ? "en" : "sv-SE",
        "author": {
            "@type": "Organization",
            "name": "Klargo",
            "url": SITE_URL
        },
        "publisher": {
            "@type": "Organization",
            "name": "Klargo",
            "url": SITE_URL,
            "logo": {
                "@type": "ImageObject",
                "url": `${SITE_URL}/klargo-logo-new.png`
            }
        },
        "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": isEnglish ? `${SITE_URL}/en/security-and-fraud` : `${SITE_URL}/sakerhet-och-bedragerier`
        },
        "description": t('security.hero.subtitle')
    };
    const howToSchema = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": t('security.emergency.title'),
        "description": t('security.emergency.subtitle'),
        "step": detailedSteps.map((step, index) => ({
            "@type": "HowToStep",
            "position": index + 1,
            "name": step.title,
            "text": step.text
        }))
    };
    const metaTitle = isEnglish
        ? "Safe private car deal: how to avoid fraud | Klargo"
        : "Säker privat bilaffär: så undviker du bedrägeri | Klargo";
    const metaDescription = isEnglish
        ? "Practical checklist and guidance for buyers and sellers to reduce the risk of fraud when buying or selling a used vehicle privately."
        : "Praktisk guide för trygg privat bilaffär. Lär dig känna igen bedrägerier, betala säkert, göra ägarbyte rätt och veta vad du gör om något känns fel.";
    const canonicalUrl = isEnglish
        ? `${SITE_URL}/en/security-and-fraud`
        : `${SITE_URL}/sakerhet-och-bedragerier`;
    const renderToc = (variant) => (<nav aria-label={t('security.toc.title')}>
      {navGroups.map((group, groupIndex) => (<div key={group.title} className={groupIndex > 0 ? 'mt-4' : ''}>
          {showGroupTitle(group) && (<div className="mb-1 pl-3 text-[12.5px] font-semibold text-ds-muted-soft">{group.title}</div>)}
          <div className="border-l border-ds-rule-row">
            {group.items.map((item) => {
                const active = activeSection === item.id;
                return (<button key={item.id} type="button" onClick={() => scrollToSection(item.id)} aria-current={active ? 'true' : undefined} className={`-ml-px block w-full border-l-2 pl-3 pr-2 text-left leading-[1.35] transition-colors ${variant === 'mobile' ? 'min-h-[44px] py-2.5 text-[14.5px]' : 'py-[7px] text-[14px]'} ${active ? 'border-ds-green font-medium text-ds-ink' : 'border-transparent text-ds-text hover:text-ds-ink'}`}>
                  {item.label}
                </button>);
            })}
          </div>
        </div>))}
    </nav>);
    return (<div className="ds-scope flex min-h-screen min-w-[320px] flex-col bg-ds-canvas font-ds-sans text-[15px] leading-[1.55] text-ds-body antialiased">
      <Helmet>
        <html lang={isEnglish ? "en" : "sv"}/>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription}/>
        <meta name="robots" content="index, follow"/>
        <link rel="canonical" href={canonicalUrl}/>
        <meta property="og:type" content="article"/>
        <meta property="og:site_name" content="Klargo"/>
        <meta property="og:locale" content={isEnglish ? "en" : "sv_SE"}/>
        <meta property="og:title" content={metaTitle}/>
        <meta property="og:description" content={metaDescription}/>
        <meta property="og:url" content={canonicalUrl}/>
        <meta property="og:image" content={`${SITE_URL}/klargo-logo-new.png`}/>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Helmet>

      <Header />

      
      
      <div id="hero" className="scroll-mt-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-8">
          <div className="border-b border-ds-rule-row pb-10 pt-8 md:pb-12 md:pt-12">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2.5 text-[13.5px] text-ds-muted">
              <Link to={isAuthenticated ? "/user/ChooseAction" : "/"} className="rounded transition-colors hover:text-ds-ink">
                {t('security.breadcrumb.home')}
              </Link>
              <RowChevron />
              <span className="text-ds-body" aria-current="page">{t('security.breadcrumb.title')}</span>
            </nav>

            <div className="mt-8 max-w-[720px] md:mt-10">
              <h1 className="m-0 font-ds-serif text-[clamp(30px,3.6vw,42px)] font-bold leading-[1.08] tracking-[-0.02em] text-ds-ink [text-wrap:balance]">
                {t('security.hero.title')}
              </h1>
              <p className="mb-0 mt-[18px] max-w-[54ch] text-[16.5px] leading-[1.6] text-ds-text">
                {t('security.hero.subtitle')}
              </p>

              <p className="mb-0 mt-5 text-[13.5px] leading-[1.6]">
                <span className="sr-only">{t('security.quickOverview.title')}: </span>
                {metadata.map((item, index) => (<span key={item.label} className="block sm:inline">
                    {index > 0 && (<span aria-hidden="true" className="hidden text-ds-muted-soft sm:inline">
                        {' · '}
                      </span>)}
                    <span className="text-ds-muted">{item.label}</span>{' '}
                    <span className="font-medium text-ds-body">{item.value}</span>
                  </span>))}
              </p>

              <div className="mt-8">
                <p className={`m-0 mb-3 ${labelClass}`}>{t('security.hero.whatYouGet')}</p>
                <TickList items={[t('security.hero.checklist1'), t('security.hero.checklist2'), t('security.hero.checklist3')]}/>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={() => scrollToSection('akut-detaljer')} className="ds-action-ink w-full justify-center sm:w-auto">
                  {t('security.hero.ctaSuspect')}
                </button>
                <button type="button" onClick={() => scrollToSection('forebygg')} className="ds-action-outline w-full justify-center sm:w-auto">
                  {t('security.hero.ctaPrevent')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      
      
      <div className="mx-auto w-full max-w-[1200px] px-6 pb-16 pt-10 md:px-8 md:pb-20 lg:pt-14">
        <div className="lg:grid lg:grid-cols-[232px_minmax(0,720px)] lg:gap-x-14">
          
          <aside className="hidden lg:block">
            <div className="sticky top-[84px] max-h-[calc(100vh-108px)] overflow-y-auto">
              <div className="mb-3 text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{t('security.toc.title')}</div>
              {renderToc('desktop')}
            </div>
          </aside>

          
          <div className="mb-10 lg:hidden">
            <Collapsible open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <CollapsibleTrigger className="flex min-h-[48px] w-full items-center justify-between border-y border-ds-rule-edge py-2 text-left text-[14.5px] font-semibold text-ds-ink transition-colors hover:text-ds-green">
                <span>{t('security.toc.title')}</span>
                <ChevronDown className={`h-[14px] w-[14px] shrink-0 stroke-[1.6] text-ds-muted transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`}/>
              </CollapsibleTrigger>
              <CollapsibleContent className="border-b border-ds-rule-edge pb-3 pt-3">
                {renderToc('mobile')}
              </CollapsibleContent>
            </Collapsible>
          </div>

          
          <main className="min-w-0">
            <article className="space-y-12 md:space-y-[72px]">
              
              <Chapter id="akut-detaljer" anchorId="misstanke" kicker={t('security.nav.emergency')} title={t('security.emergency.title')} lead={t('security.emergency.subtitle')} emphasis>
                <ol className="m-0 list-none p-0">
                  {detailedSteps.map((step, index) => (<li key={step.id} className="grid grid-cols-[40px_minmax(0,1fr)] gap-x-3 border-b border-ds-rule-row py-4 md:grid-cols-[56px_minmax(0,1fr)] md:gap-x-4 md:py-5">
                      <span className={`${numClass} pt-[5px]`}>{`0${index + 1}`}</span>
                      <div>
                        <h3 className="m-0 font-ds-serif text-[19px] font-semibold leading-[1.3] tracking-[-0.005em] text-ds-ink">
                          {step.title}
                        </h3>
                        <p className={`mb-0 mt-2 ${proseClass}`}>{step.text}</p>
                      </div>
                    </li>))}
                </ol>
              </Chapter>

              
              <Chapter id="seriost-vs-bedragare" kicker={t('security.nav.beforeDeal')} title={t('security.legitimateVsFraud.title')} lead={t('security.legitimateVsFraud.subtitle')}>
                <div className="grid gap-4 pt-6 md:grid-cols-2">
                  <Callout tone="positive" label={t('security.legitimateVsFraud.saferContact')} items={[
            t('security.legitimateVsFraud.safe1'),
            t('security.legitimateVsFraud.safe2'),
            t('security.legitimateVsFraud.safe3')
        ]}/>
                  <Callout tone="warning" label={t('security.legitimateVsFraud.warningSignals')} items={[
            t('security.legitimateVsFraud.warning1'),
            t('security.legitimateVsFraud.warning2'),
            t('security.legitimateVsFraud.warning3'),
            t('security.legitimateVsFraud.warning4')
        ]}/>
                </div>

                <div className={`mt-6 space-y-3 ${proseClass}`}>
                  <p className="m-0">{t('security.legitimateVsFraud.info1')}</p>
                  <p className="m-0">{t('security.legitimateVsFraud.info2')}</p>
                  <p className="m-0">{t('security.legitimateVsFraud.info3')}</p>
                </div>
              </Chapter>

              
              <Chapter id="forebygg" anchorId="grundregler" title={t('security.fourRules.title')} lead={t('security.fourRules.subtitle')}>
                <ol className="m-0 grid list-none p-0 md:grid-cols-2">
                  {fourRules.map((rule, index) => (<li key={rule.title} className={`flex flex-col border-ds-rule-row pb-5 pt-4 md:pb-6 md:pt-5 ${RULE_CELLS[index]}`}>
                      <span className={numClass}>{`0${index + 1}`}</span>
                      <h3 className="mb-0 mt-2.5 font-ds-serif text-[18px] font-semibold leading-[1.3] tracking-[-0.005em] text-ds-ink">
                        {rule.title}
                      </h3>
                      <ul className="mb-0 mt-2.5 list-disc space-y-1.5 pl-[18px] text-[14.5px] leading-[1.55] text-ds-text marker:text-ds-muted-soft">
                        {rule.points.map((point) => (<li key={point}>{point}</li>))}
                      </ul>
                    </li>))}
                </ol>
              </Chapter>

              
              <Chapter id="checklista-kopa" title={t('security.checklists.title')} lead={t('security.checklists.subtitle')}>
                <div className="grid gap-y-10 pt-6 md:grid-cols-2 md:gap-x-10">
                  <ChecklistLedger title={t('security.checklists.buyerTitle')} items={buyerChecklist}/>
                  <ChecklistLedger title={t('security.checklists.sellerTitle')} items={sellerChecklist}/>
                </div>
              </Chapter>

              
              <Chapter id="vanliga-bedragerier" title={t('security.commonFrauds.title')} lead={t('security.commonFrauds.subtitle')}>
                <Accordion type="single" collapsible>
                  {fraudTypes.map((fraud, i) => (<AccordionItem key={fraud.title} value={`fraud-${i}`} className={registerItemClass}>
                      <AccordionTrigger className={registerTriggerClass}>
                        <span className={registerTitleClass}>{fraud.title}</span>
                      </AccordionTrigger>
                      <AccordionContent className={registerContentClass}>
                        <p className="m-0">{fraud.desc}</p>
                        <p className={`m-0 mt-4 ${labelClass}`}>{t('security.commonFrauds.howToProtect')}</p>
                        <TickList items={fraud.tips} className="mt-2"/>
                      </AccordionContent>
                    </AccordionItem>))}
                </Accordion>
              </Chapter>

              
              <Chapter id="digital-sakerhet" title={t('security.digitalSecurity.title')} lead={t('security.digitalSecurity.subtitle')}>
                <div className="pt-6">
                  <p className={`m-0 mb-3 ${labelClass}`}>{t('security.digitalSecurity.basicAdvice')}</p>
                  <TickList items={[
            t('security.digitalSecurity.advice1'),
            t('security.digitalSecurity.advice2'),
            t('security.digitalSecurity.advice3'),
            t('security.digitalSecurity.advice4')
        ]}/>
                </div>

                <div className={`mt-6 space-y-3 ${proseClass}`}>
                  <p className="m-0">{t('security.digitalSecurity.info1')}</p>
                  <p className="m-0">{t('security.digitalSecurity.info2')}</p>
                </div>
              </Chapter>

              
              <Chapter id="skulder-rattigheter" title={t('security.debtsRights.title')}>
                <div className="pt-6">
                  <p className={`m-0 mb-3 ${labelClass}`}>{t('security.debtsRights.shortVersion')}</p>
                  <TickList items={[t('security.debtsRights.point1'), t('security.debtsRights.point2'), t('security.debtsRights.point3')]}/>
                </div>

                <div className="mt-6 border-l-2 border-ds-ink pl-4">
                  <h3 className={`m-0 ${labelClass}`}>{t('security.debtsRights.alwaysSave')}</h3>
                  <ul className="mb-0 mt-2 flex list-none flex-wrap gap-x-6 gap-y-1 p-0 text-[14.5px] leading-[1.5] text-ds-text">
                    {[t('security.debtsRights.save1'), t('security.debtsRights.save2'), t('security.debtsRights.save3'), t('security.debtsRights.save4')].map((item) => (<li key={item}>{item}</li>))}
                  </ul>
                </div>

                <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen} className="mt-6">
                  <CollapsibleTrigger className="flex min-h-[48px] w-full items-center justify-between border-y border-ds-rule-row text-left text-[14.5px] font-semibold text-ds-ink transition-colors hover:text-ds-green">
                    <span>{detailsOpen ? t('security.showLess') : t('security.showMore')}</span>
                    <ChevronDown className={`h-[14px] w-[14px] shrink-0 stroke-[1.6] text-ds-muted transition-transform ${detailsOpen ? 'rotate-180' : ''}`}/>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className={`space-y-3 border-b border-ds-rule-row pb-6 pt-5 ${proseClass}`}>
                      <p className="m-0">{t('security.debtsRights.details1')}</p>
                      <p className="m-0">{t('security.debtsRights.details2')}</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Chapter>

              
              <Chapter id="hur-klargo-hjalper" kicker={t('security.nav.rightsSupport')} title={t('security.klargoHelps.title')} lead={t('security.klargoHelps.subtitle')}>
                <div className="grid gap-y-8 pt-6 md:grid-cols-2 md:gap-x-10">
                  <div>
                    <h3 className={`m-0 mb-3 ${labelClass}`}>{t('security.klargoHelps.withKlargo')}</h3>
                    <TickList items={[t('security.klargoHelps.feature1'), t('security.klargoHelps.feature2'), t('security.klargoHelps.feature3')]}/>
                  </div>
                  <div className="border-l-2 border-ds-ink pl-4">
                    <h3 className={`m-0 ${labelClass}`}>{t('security.klargoHelps.important')}</h3>
                    <ul className="mb-0 mt-2.5 list-disc space-y-1.5 pl-[18px] text-[14.5px] leading-[1.5] text-ds-text marker:text-ds-muted-soft">
                      {[t('security.klargoHelps.note1'), t('security.klargoHelps.note2'), t('security.klargoHelps.note3')].map((item) => (<li key={item}>{item}</li>))}
                    </ul>
                  </div>
                </div>

                <div className={`mt-6 space-y-3 ${proseClass}`}>
                  <p className="m-0">{t('security.klargoHelps.info1')}</p>
                  <p className="m-0">{t('security.klargoHelps.info2')}</p>
                </div>
              </Chapter>

              
              <Chapter id="faq-bedrageri" title={t('security.faq.title')}>
                <Accordion type="single" collapsible>
                  {faqItems.map((item, i) => (<AccordionItem key={item.q} value={`faq-${i}`} className={registerItemClass}>
                      <AccordionTrigger className={registerTriggerClass}>
                        <span className={registerTitleClass}>{item.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className={registerContentClass}>{item.a}</AccordionContent>
                    </AccordionItem>))}
                </Accordion>
              </Chapter>

              
              <Chapter id="ordlista" title={t('security.glossary.title')}>
                <dl className="m-0">
                  {glossary.map((item) => (<div key={item.term} className="grid gap-x-8 border-b border-ds-rule-row py-3.5 sm:grid-cols-[200px_minmax(0,1fr)]">
                      <dt className="text-[15px] font-semibold leading-[1.5] text-ds-ink">{item.term}</dt>
                      <dd className="m-0 mt-0.5 text-[15px] leading-[1.55] text-ds-text sm:mt-0">{item.def}</dd>
                    </div>))}
                </dl>
              </Chapter>
            </article>

            
            <footer className="mt-12 border-t border-ds-rule-edge pt-6 md:mt-[72px]">
              <h2 className="m-0 text-[15px] font-semibold leading-[1.4] text-ds-ink">{t('security.sources.title')}</h2>
              <div className="mt-3 max-w-[62ch] space-y-2.5 text-[14px] leading-[1.6] text-ds-muted [text-wrap:pretty]">
                <p className="m-0">{t('security.sources.info1')}</p>
                <p className="m-0">{t('security.sources.info2')}</p>
                <p className="m-0">{t('security.sources.info3')}</p>
              </div>
              <Link to="/hjalp" className="ds-action-text mt-2">
                {t('security.sources.contactUs')}
                <ForwardArrow stroke="currentColor" width={14} height={11}/>
              </Link>
            </footer>
          </main>
        </div>
      </div>

      
      
      <section aria-labelledby="security-close-heading" className="border-y border-ds-rule-row bg-ds-sheet py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-6 md:px-8">
          <div className="border-t-2 border-ds-ink pt-7 md:flex md:items-end md:justify-between md:gap-14">
            <div className="max-w-[600px]">
              <h2 id="security-close-heading" className="m-0 font-ds-serif text-[clamp(26px,2.9vw,34px)] font-bold leading-[1.1] tracking-[-0.015em] text-ds-ink">
                {t('Gör nästa fordonsaffär i Klargo')}
              </h2>
              <p className="mb-0 mt-3.5 text-[15.5px] leading-[1.55] text-ds-text [text-wrap:pretty]">
                {t('Från överenskommelse till genomförd affär.')}{' '}
                <span className="font-medium text-ds-ink">{t('Gratis för både köpare och säljare.')}</span>
              </p>
            </div>
            <div className="mt-7 flex md:mt-0 md:ml-auto md:flex-none md:pb-[3px]">
              <button type="button" onClick={() => setEntryOpen(true)} className="ds-action-ink w-full justify-center whitespace-nowrap sm:w-auto">
                <span>{t('Kom igång')}</span>
                <ForwardArrow />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer ground="canvas"/>

      <LoginDialog open={entryOpen} onOpenChange={setEntryOpen} intent="start"/>
    </div>);
};
export default SecurityAndFraud;
